import { useMemo } from 'react';
import { DataTable, type DataTableFeatures } from '@customerio/pluma-components/react';
import type { ColumnDef } from '@tanstack/react-table';
import type { Tag } from '@customerio/hydra-ui-api';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { AttributeScope, DataIndexTotals } from '@/services/db/data-index/entity';
import { DATA_INDEX_PAGE_SIZE } from '@/services/db/data-index/entity';
import { IMMUTABLE_CUSTOMER_ATTRIBUTES, type IdentifiersConfig } from '@/features/people';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import type { DataIndexItemKind } from '../utils/types';
import type { DataIndexTableRow } from '../utils/table-row';
import { ObjectHeader, PeopleHeader, SourcesHeader } from './column-headers';
import { PercentageCell } from './percentage-cell';
import { SourcesCell } from './sources-cell';
import { UsageCell } from './usage-cell';
import { usageSummary, type UsageCounts } from '../utils/usage-summary';
import { isRowSelectable } from './row-selection';
import { DataIndexTableFrame, useSearchDraft } from './table-shell';
import type { DataIndexBulkActionsConfig } from './bulk-actions';
import { EditableCellsProvider, NameEditorCell, TagsEditorCell, type EditableCellsBulk } from './editable-cells';
import {
  dataIndexFilters,
  dataIndexSavedViews,
  type DataIndexFilterCatalogue,
  type DataIndexNarrowingControls,
} from '../utils/toolbar';

export type DataIndexTableProps = {
  workspaceId: number;
  kind: DataIndexItemKind;
  scope: AttributeScope;
  rows: DataIndexTableRow[];
  tags: readonly Tag[];
  identifiers: IdentifiersConfig | null | undefined;
  /** Whether the workspace identifies people by more than one attribute. */
  isMultiIdentifier: boolean;
  isLoading: boolean;
  /** What narrows the list, and how to narrow it differently. */
  narrowing: DataIndexNarrowingControls;
  /** Every tag and source the rows in scope were written with. */
  catalogue: DataIndexFilterCatalogue;
  /** How many rows each view holds, or null until the totals read lands. */
  totals: DataIndexTotals | null;
  page: number;
  onPageChange: (page: number) => void;
  rowSelection: Record<string, boolean>;
  onRowSelectionChange: (selection: Record<string, boolean>) => void;
  /** Where each row is used, by name. Absent until its own read lands. */
  rowUsage: Record<string, UsageCounts> | undefined;
  isRowUsageLoading: boolean;
  /** Opens one row's details beside the list. */
  onOpenRow: (name: string) => void;
  /** How many people carry each row, by name. A name the backend skipped is absent. */
  profileCounts: Record<string, number> | undefined;
  /** Whether the denominator every share divides by is still being read. */
  isTotalPeopleLoading: boolean;
  /** Whether the rounds that fill the counts in are still running. */
  isProfileCountsFetching: boolean;
  totalPeople: number;
  objectCounts: Record<string, number> | undefined;
  isObjectCountsLoading: boolean;
  totalObjects: number;
  objectTypeName: string;
  /** Saves one row's description; resolves to whether it went through. */
  onSaveDescription: (name: string, description: string) => Promise<boolean>;
  /** Saves one row's tags; resolves to whether it went through. */
  onSaveTags: (name: string, tagIds: number[]) => Promise<boolean>;
  /** What can be done to the selection. */
  bulkActions: DataIndexBulkActionsConfig;
  /** Set while several rows are being edited together. */
  bulkEdit: DataIndexBulkEdit | null;
};

/** The rows being edited together, and the values they are being given. */
export type DataIndexBulkEdit = EditableCellsBulk & {
  names: readonly string[];
};

/** Only person attributes and events are counted against the workspace's people. */
function withPeopleColumn(scope: AttributeScope): boolean {
  return scope.kind === 'profile';
}

/** Only an object's own attributes are counted against its objects. */
function withObjectColumn(scope: AttributeScope): boolean {
  return scope.kind === 'object';
}

/**
 * How many rows the current view holds, which is what the pager counts. The
 * in-use view is a different set from the whole list, so it has its own
 * total — and its own way of being unknown, because the backend budgets the
 * usage fill that count needs.
 */
function paginationTotal(totals: DataIndexTotals | null, inUseOnly: boolean): number | undefined {
  if (totals == null) {
    return undefined;
  }
  return inUseOnly ? (totals.in_use ?? undefined) : totals.all;
}

export function DataIndexTable(props: DataIndexTableProps) {
  const columns = useDataIndexColumns(props);
  const editable = useEditableCellsValue(props);
  const { narrowing, catalogue, totals, bulkEdit } = props;
  const isEventBrowser = props.kind === 'event';
  const draft = useSearchDraft(narrowing.search);

  // Both rebuild the table's filter context, which re-renders every row, so
  // they are built once per change rather than once per render.
  const filters = useMemo(
    () =>
      dataIndexFilters(catalogue, narrowing, {
        campaignsPlural: 'Automations',
        newsletterSingular: 'One-time send',
      }),
    [catalogue, narrowing],
  );
  const savedViews = useMemo(
    () => dataIndexSavedViews(isEventBrowser, narrowing, totals),
    [isEventBrowser, narrowing, totals],
  );

  // While several rows are edited together, they are the only ones shown:
  // the rest of the page is not what anyone is looking at.
  const rows = bulkEdit == null ? props.rows : props.rows.filter((row) => bulkEdit.names.includes(row.id));

  return (
    <EditableCellsProvider value={editable}>
      <DataTable
        caption={isEventBrowser ? 'List of events' : 'List of attributes'}
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={props.isLoading}
        // A bulk edit shows only the rows being edited, so narrowing them
        // further would strand it: the edit would still be open over rows
        // nobody can see.
        withSearch={bulkEdit == null}
        search={{
          value: narrowing.search,
          inputValue: draft.text,
          onSearchInput: draft.setText,
          onSearch: narrowing.onSearchChange,
          shouldSearchOnEnter: true,
          shouldSearchOnClear: true,
          isManual: true,
        }}
        withFilters={bulkEdit == null}
        filters={filters}
        withSavedViews={bulkEdit == null}
        savedViews={savedViews}
        withPagination={bulkEdit == null}
        pagination={{
          page: props.page,
          pageSize: DATA_INDEX_PAGE_SIZE,
          totalItemCount: paginationTotal(totals, narrowing.inUseOnly),
          onPageChange: props.onPageChange,
          isManual: true,
        }}
        withRowSelection={bulkEdit == null}
        getRowCanSelect={(row) => isRowSelectable(props.kind, row.original.id, props.identifiers)}
        rowSelection={bulkEdit == null ? props.rowSelection : {}}
        onRowSelectionChange={props.onRowSelectionChange}
        bulkActions={props.bulkActions}
        emptyState={{ title: props.kind === 'event' ? 'No events yet' : 'No attributes yet', icon: 'data-index' }}
      >
        <DataIndexTableFrame />
      </DataTable>
    </EditableCellsProvider>
  );
}

/** What the two editing cells read, which changes while they stay mounted. */
function useEditableCellsValue(props: DataIndexTableProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('journeys/attribute_metadata/edit');
  const {
    workspaceId,
    kind,
    scope,
    identifiers,
    isMultiIdentifier,
    tags,
    bulkEdit,
    onSaveDescription,
    onSaveTags,
    onOpenRow,
  } = props;

  return useMemo(
    () => ({
      workspaceId,
      kind,
      scope,
      identifiers,
      isMultiIdentifier,
      tags,
      canEdit,
      bulk: bulkEdit,
      onSaveDescription,
      onSaveTags,
      onOpenRow,
    }),
    [
      bulkEdit,
      canEdit,
      identifiers,
      isMultiIdentifier,
      kind,
      onOpenRow,
      onSaveDescription,
      onSaveTags,
      scope,
      tags,
      workspaceId,
    ],
  );
}

function useDataIndexColumns(props: DataIndexTableProps): ColumnDef<DataTableFeatures, DataIndexTableRow>[] {
  // Object and relationship attributes only show the content surfaces where
  // the workspace has them turned on; the page this replaces reads the same
  // flag for the same cell.
  const withContentUsage = useFeatureFlag(AccountFeatureFlag.ShowTemplatesSnippetsLayouts);
  const {
    kind,
    scope,
    rowUsage,
    isRowUsageLoading,
    profileCounts,
    isTotalPeopleLoading,
    isProfileCountsFetching,
    totalPeople,
    objectCounts,
    isObjectCountsLoading,
    totalObjects,
    objectTypeName,
  } = props;

  return useMemo(() => {
    const usageTerms = { campaignSingular: 'automation' };
    const personSingular = 'person';

    const columns: ColumnDef<DataTableFeatures, DataIndexTableRow>[] = [
      {
        id: 'id',
        header: 'Name',
        enableHiding: false,
        // A component, not an inline function: see `editable-cells`.
        cell: NameEditorCell,
      },
      {
        id: 'sources',
        header: () => <SourcesHeader />,
        maxSize: 120,
        cell: ({ row }) => (
          // An attribute Customer.io maintains has no source anyone chose, so
          // it shows the empty state rather than a mark for the system.
          <SourcesCell sources={IMMUTABLE_CUSTOMER_ATTRIBUTES.includes(row.original.id) ? [] : row.original.sources} />
        ),
      },
      {
        id: 'tags',
        header: 'Tags',
        maxSize: 150,
        cell: TagsEditorCell,
      },
      {
        id: 'usage',
        header: 'Usage',
        maxSize: 100,
        cell: ({ row }) => (
          <UsageCell
            summary={usageSummary(kind, rowUsage?.[row.original.id], usageTerms, withContentUsage)}
            isLoading={isRowUsageLoading}
          />
        ),
      },
    ];

    if (withPeopleColumn(scope)) {
      columns.push({
        id: 'people',
        header: () => <PeopleHeader isEventBrowser={kind === 'event'} />,
        maxSize: 80,
        cell: ({ row }) => (
          <PercentageCell
            matching={profileCounts?.[row.original.id]}
            total={totalPeople}
            isLoading={isTotalPeopleLoading || (profileCounts?.[row.original.id] == null && isProfileCountsFetching)}
            noun={personSingular}
            verb={kind === 'event' ? 'have performed this event' : 'have this attribute'}
          />
        ),
      });
    }

    if (withObjectColumn(scope)) {
      columns.push({
        id: 'objects',
        header: () => <ObjectHeader objectTypeName={objectTypeName} />,
        maxSize: 80,
        cell: ({ row }) => (
          <PercentageCell
            // The endpoint answers for every attribute of the type in one
            // pass, so once it has answered, a name it omits has none. Before
            // that — and if it fails — the count is unknown, which the cell
            // shows as a dash rather than as zero.
            matching={objectCounts == null ? undefined : (objectCounts[row.original.id] ?? 0)}
            total={totalObjects}
            isLoading={isObjectCountsLoading}
            noun={objectTypeName}
            verb="have this attribute"
          />
        ),
      });
    }

    return columns;
  }, [
    isObjectCountsLoading,
    isProfileCountsFetching,
    isTotalPeopleLoading,
    isRowUsageLoading,
    kind,
    objectCounts,
    objectTypeName,
    profileCounts,
    rowUsage,
    scope,
    totalObjects,
    totalPeople,
    withContentUsage,
  ]);
}
